import { NavSectionTranslations, NavValueTranslations, RoutePaths } from '../../shared/api/types/enums';
import { RAIL_WORLDS, RailWorld } from '../nav-data.constant';

/**
 * The top bar's SECTION label (NBO-2388, first consumer NBO-2390).
 *
 * V4 decided that a redesigned screen has no page-title row: the app top bar's
 * left slot — deliberately empty since the wave-0.5 shell — carries the
 * section name instead. One treatment, never restyled per route, never a
 * count; the string is resolved from the rail's own tree below, so it is the
 * SAME string the rail renders for that destination by construction — the two
 * cannot drift ("Products & Clusters" in the rail can never become "Products"
 * in the top bar).
 *
 * GATING IS THE POINT. The label renders ONLY for routes that have adopted
 * the new shell. Legacy screens keep their own page header and the left side
 * stays empty — which is what lets the shell chrome deploy to production
 * before any listing does. Adding a route here is therefore part of that
 * screen's adoption ticket, never a sweep: a route listed before its title
 * row is removed shows the name twice.
 *
 * TWO QUESTIONS, TWO CONTRACTS. `isNewShellRoute` answers "has this route
 * adopted the shell?" from ADOPTED_ROUTES alone — pure and synchronous, which
 * the navigation.service boot seed depends on (it runs from
 * `window.location.pathname` before roles resolve) and which paints the
 * canvas. `sectionLabelKeyForUrl` answers "what does the rail call it?" and
 * takes the tree to read: the top bar passes the VIEWER-filtered tree
 * (`RailTreeService.visibleWorlds()`), so a role-collapsed section labels the
 * same way in both places; the structural RAIL_WORLDS is only the fallback for
 * a route the viewer's rail does not show at all.
 *
 * Extend ADOPTED_ROUTES and its spec table together (the pattern
 * `chromeVisibilityForUrl` established in navigation.service.ts). An adopted
 * route must be a rail destination — the spec's completeness guard fails on
 * one that resolves to no rail label.
 */

/**
 * Routes that have adopted the new listing shell, matched boundary-safe
 * against the URL path so a child route keeps its section's label.
 *
 * Home (NBO-2390) and the admin batch (NBO-2338) so far; NBO-2339…2342 each add their own.
 *
 * A PREFIX ADOPTS EVERY CHILD ROUTE UNDER IT. That is right for details that open as a
 * `.full-screen-edit` overlay (they paint over the canvas) and wrong for a child that is an
 * ordinary in-flow page: it silently gets the grey canvas and loses the 32px shell inset.
 * Check each adopted screen's child routes and list the in-flow ones in
 * UNADOPTED_CHILD_ROUTES below.
 */
export const ADOPTED_ROUTES: RoutePaths[] = [
  RoutePaths.Home,
  RoutePaths.AdminAgents,
  RoutePaths.UserManagementLoginTokens,
  RoutePaths.AdminBackofficeUsers,
  RoutePaths.PimClusterDefinition,
  RoutePaths.SacTemplates,
  RoutePaths.AdminValueset,
  RoutePaths.AdminApiKeys,
  RoutePaths.AdminSearchIndex,
  RoutePaths.AdminAttributes,
  RoutePaths.Companies,
  RoutePaths.UserManagementOrderlists,
  RoutePaths.UserManagementFavorites,
  RoutePaths.UserManagementUsers,
  RoutePaths.SacCarriers,
  RoutePaths.SacPaymentMethods,
  RoutePaths.SacWarehouses,
  RoutePaths.SacOrderStatuses,
  // One screen for all five rule types (NBO-2446). The component's fill gate reads
  // this table via isNewShellRoute; the old per-type child URLs only redirect here.
  RoutePaths.SacBusinessRules,
  // Pricing + shop-and-channels batch (NBO-2341). Details are dialogs or
  // .full-screen-edit overlays on every one of these, so the prefix match is safe.
  RoutePaths.PricingTaxes,
  RoutePaths.PricingSurcharges,
  // Pricesheet details (`new`, `:id`) are .full-screen-edit overlays — prefix-safe.
  RoutePaths.PricingPricesheets,
  RoutePaths.SacEventActions,
  RoutePaths.SacChannels,
  // The last adoption of the wave (NBO-2342). Product and cluster details (`product/…`,
  // `cluster/…`) are sibling routes under this prefix that render .full-screen-edit overlays.
  RoutePaths.PimProductsAndClusters,
  // Category management (the tree rail + children listing). Its details child route
  // (`pim/categories/:id`) is a .full-screen-edit overlay, so it paints over the canvas.
  RoutePaths.PimCategories,
  // The order listings (NBO-2458). Every child route under these prefixes (`:id`, `:id/edit`,
  // `new`) is the order editor, a .full-screen-edit overlay — prefix-safe. The editor keeps the
  // top bar (rail-only chromeless), so it shows the section label too; accepted 2026-10-02.
  RoutePaths.OrdersSp,
  RoutePaths.QuotesSp,
  RoutePaths.RequestsSp,
  RoutePaths.OrdersBo,
  // The Action Hub's "Your day" queue (NBO-2542): cards on the canvas, no listing card. It has no
  // child routes.
  RoutePaths.AiActionFeed
];

/**
 * In-flow child pages that sit UNDER an adopted prefix but have not adopted the shell —
 * carved back out, so they keep the legacy white ground, the 32px inset and no label.
 * Boundary-safe prefixes, same matching as ADOPTED_ROUTES.
 *
 * - templates `error/logs/:id` — EventTemplateDetailsComponent, a plain mat-card page
 *   reached from the Logs tab (NBO-2338 adopted the `templates` prefix above it).
 */
export const UNADOPTED_CHILD_ROUTES: string[] = [`${RoutePaths.SacTemplates}/error/logs`];

interface RailLabel {
  path: string;
  labelKey: string;
}

/**
 * The rail's string per destination for a given tree — mirroring the branch
 * shell-rail.component.html takes: a single-item section is a plain link row
 * labeled with the SECTION string, a multi-item section labels each ITEM with
 * its own string. Longest path first so a more specific route wins over a
 * prefix of it.
 */
function railLabels(worlds: RailWorld[]): RailLabel[] {
  return worlds
    .flatMap((world) =>
      world.sections.flatMap((section) =>
        section.items.map((item) => ({
          path: item.path,
          labelKey: section.items.length <= 1 ? NavSectionTranslations[section.key] : NavValueTranslations[item.value]
        }))
      )
    )
    .sort((a, b) => b.path.length - a.path.length);
}

/** The structural tree's labels, built once — the fallback and the spec's baseline. */
const STRUCTURAL_LABELS: RailLabel[] = railLabels(RAIL_WORLDS);

/**
 * Whether the route has adopted the new shell. Drives everything that
 * adoption means for the frame — today the section label in the top bar and
 * the grey canvas with no shell inset (NBO-2390) — from ONE table, so a route
 * cannot get the label without the canvas or the other way round.
 *
 * Pure on purpose: reads ADOPTED_ROUTES only, so it can run before roles
 * resolve (the boot seed) and never depends on what the viewer may see.
 */
export function isNewShellRoute(url: string): boolean {
  const path = normalizePath(url);
  if (UNADOPTED_CHILD_ROUTES.some((route) => matchesPath(path, route))) {
    return false;
  }
  return ADOPTED_ROUTES.some((route) => matchesPath(path, route));
}

/**
 * The section label for a URL, or null when the route has not adopted the new
 * shell (the overwhelming majority today) — the caller renders nothing.
 *
 * `worlds` is the tree to read the label from. The top bar passes the viewer-
 * filtered tree so the label matches what the rail renders for THIS viewer;
 * a route the viewer's tree does not hold falls back to the structural label,
 * so an adopted screen is never nameless.
 */
export function sectionLabelKeyForUrl(url: string, worlds: RailWorld[] = RAIL_WORLDS): string | null {
  if (!isNewShellRoute(url)) {
    return null;
  }
  const path = normalizePath(url);
  return railLabelKeyForPath(path, worlds) ?? railLabelKeyForPath(path);
}

/**
 * The string the rail shows for the nav destination owning this path in the
 * given tree, or null for a path that tree holds no destination for. Exported
 * for the spec — consumers go through sectionLabelKeyForUrl, which applies the
 * adoption gate and the structural fallback.
 */
export function railLabelKeyForPath(path: string, worlds: RailWorld[] = RAIL_WORLDS): string | null {
  const labels = worlds === RAIL_WORLDS ? STRUCTURAL_LABELS : railLabels(worlds);
  return labels.find((entry) => matchesPath(path, entry.path))?.labelKey ?? null;
}

/** Boundary-safe prefix match: `bo/orders` owns `bo/orders/5`, never `bo/orders-x`. */
function matchesPath(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Strips query string, fragment and the leading/trailing slashes. */
function normalizePath(url: string): string {
  return (url ?? '')
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
}
